import { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import MovieCard from "@/components/feature/MovieCard";
import FilterSidebar from "@/pages/category/components/FilterSidebar";
import { useSeo } from "@/hooks/useSeo";
import { filterMovies } from "@/utils/movie";
import { sortOptions } from "@/mocks/site";

const PAGE_SIZE = 6;

function parseList(value: string | null): string[] {
  if (!value) return [];
  return value.split(",").map((v) => v.trim()).filter(Boolean);
}

export default function Category() {
  const [params, setParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);

  const selection = useMemo(
    () => ({
      types: parseList(params.get("type")),
      regions: parseList(params.get("region")),
      years: parseList(params.get("year")),
      tags: parseList(params.get("tag")),
    }),
    [params]
  );

  const sort = params.get("sort") || "latest";
  const page = Math.max(1, Number(params.get("page")) || 1);

  useSeo({
    title: "影视分类 - 欲望的诱惑",
    description:
      "浏览欲望的诱惑中的最新影视分类，快速找到喜欢的电影、电视剧及更多影视内容。",
    keywords: "影视分类,电影分类,电视剧分类,欲望的诱惑,影视筛选",
    canonicalPath: "/category",
  });

  const filtered = useMemo(
    () => filterMovies({ ...selection, sort }),
    [selection, sort]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageList = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next);
  };

  const toggleFilter = (key: "types" | "regions" | "years" | "tags", value: string) => {
    const paramKey = { types: "type", regions: "region", years: "year", tags: "tag" }[key];
    const current = selection[key];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateParam(paramKey, next.length ? next.join(",") : null);
  };

  const resetFilters = () => setParams(new URLSearchParams());

  const selectedChips = [
    ...selection.types.map((v) => ({ key: "type", v })),
    ...selection.regions.map((v) => ({ key: "region", v })),
    ...selection.years.map((v) => ({ key: "year", v })),
    ...selection.tags.map((v) => ({ key: "tag", v })),
  ];

  const removeChip = (key: string, value: string) => {
    const map = { type: "types", region: "regions", year: "years", tag: "tags" } as const;
    toggleFilter(map[key as keyof typeof map], value);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <PageHeader
          title="影视分类"
          description="按类型、地区、年份与热门标签多维度筛选，快速定位你想看的影视作品。"
          crumbs={[{ label: "首页", path: "/" }, { label: "影视分类" }]}
        />

        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
          <button
            onClick={() => setShowFilters((v) => !v)}
            className="lg:hidden w-full flex items-center justify-between px-4 py-3 mb-4 rounded-lg bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-800 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-primary-500"></i>
              筛选条件
            </span>
            <i className={`${showFilters ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-lg`}></i>
          </button>

          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <aside className={`w-full lg:w-[270px] shrink-0 ${showFilters ? "block" : "hidden"} lg:block`}>
              <div className="lg:sticky lg:top-24">
                <FilterSidebar selection={selection} onToggle={toggleFilter} onReset={resetFilters} />
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              {/* toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 mb-5 border-b border-background-200/70">
                <p className="text-sm text-foreground-600">
                  共找到 <span className="text-primary-600 font-semibold">{filtered.length}</span> 部影视作品
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500 hidden sm:inline">排序：</span>
                  <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                    {sortOptions.map((o) => (
                      <button
                        key={o.key}
                        onClick={() => updateParam("sort", o.key === "latest" ? null : o.key)}
                        className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                          sort === o.key
                            ? "bg-primary-500 text-background-50"
                            : "text-foreground-600 hover:text-primary-600"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {selectedChips.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  <span className="text-xs text-foreground-500">已选：</span>
                  {selectedChips.map((chip) => (
                    <button
                      key={`${chip.key}-${chip.v}`}
                      onClick={() => removeChip(chip.key, chip.v)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer"
                    >
                      {chip.v}
                      <i className="ri-close-line"></i>
                    </button>
                  ))}
                  <button
                    onClick={resetFilters}
                    className="text-xs text-foreground-500 hover:text-primary-600 cursor-pointer underline-offset-2 hover:underline"
                  >
                    清空
                  </button>
                </div>
              )}

              {pageList.length > 0 ? (
                <>
                  <div className="space-y-3 md:space-y-4">
                    {pageList.map((m: any) => (
                      <MovieCard key={m.id} movie={m} variant="list" />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-1.5 mt-8">
                      <button
                        onClick={() => updateParam("page", String(currentPage - 1))}
                        disabled={currentPage <= 1}
                        className="w-9 h-9 rounded-md flex items-center justify-center border border-background-300/70 text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 hover:text-primary-600 cursor-pointer"
                        aria-label="上一页"
                      >
                        <i className="ri-arrow-left-s-line"></i>
                      </button>
                      {Array.from({ length: totalPages }).map((_, i) => (
                        <button
                          key={i}
                          onClick={() => updateParam("page", String(i + 1))}
                          className={`w-9 h-9 rounded-md text-sm transition-colors cursor-pointer ${
                            currentPage === i + 1
                              ? "bg-primary-500 text-background-50"
                              : "border border-background-300/70 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => updateParam("page", String(currentPage + 1))}
                        disabled={currentPage >= totalPages}
                        className="w-9 h-9 rounded-md flex items-center justify-center border border-background-300/70 text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 hover:text-primary-600 cursor-pointer"
                        aria-label="下一页"
                      >
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="py-20 text-center">
                  <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-inbox-archive-line text-3xl text-foreground-400"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
                    暂无相关影视内容
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-500">
                    没有符合当前筛选条件的作品，试着调整或重置筛选条件吧。
                  </p>
                  <button
                    onClick={resetFilters}
                    className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line"></i>
                    重置筛选
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="mt-12 p-6 rounded-lg bg-background-100 border border-background-200/70 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-foreground-600">
              没找到想看的？试试用关键词直接搜索影视名称、导演或演员。
            </p>
            <Link
              href="/search"
              to="/search"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-search-line"></i>
              前往搜索
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}